import React, { ReactElement, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import produce from 'immer';
import { connect } from 'react-redux';

import {
	AppState,
	AppThunkDispatch,
	BosatlasActions,
	bosatlasAddToBundle,
	bosatlasClearBundle,
	bosatlasComparisonDelete,
	bosatlasComparisonUpdate,
	bosatlasComparisonUpdateBundle,
	bosatlasFetchBundle,
	bosatlasProjectDelete,
	bosatlasProjectUpdate,
	bosatlasProjectUpdateBundle,
	bosatlasRemoveComparisonFromBundle,
	bosatlasRemoveProjectFromBundle,
	bosatlasUpdateBundle,
	Bundle,
	bundleDtoToBundle,
	SelectedComparison,
	SelectedProject
} from '../../../redux';
import { ProjectDto, ProjectComparisonDto } from '../../../models';
import {
	ITextInputDialogProps,
	ISelectBundleDialogProps,
	TextInputDialog,
	SelectBundleDialog,
	PageContainer,
	BackButton,
	DialogWindow,
	ProjectSelector
} from '../../molecules';
import { BundleViewer } from '../../organisms';

import './bundle-content-page.styles.scss';
import { LoadingContainer } from '../../atoms';
import { useTranslation } from 'react-i18next';

export interface IBundleContentPageStateProps {
	bundle?: Bundle;
	selectedProjects: SelectedProject[];
	selectedComparisons: SelectedComparison[];
	updateBundle: (bundleId: string, title: string) => void;
	fetchBundleContent: (bundleId: string) => void;
	updateProject: (project: ProjectDto) => void;
	updateProjectBundle: (
		project: ProjectDto,
		bundles: Bundle[],
		callback?: () => void
	) => void;
	updateComparison: (comparison: ProjectComparisonDto) => void;
	updateComparisonBundle: (
		comparison: ProjectComparisonDto,
		bundles: Bundle[],
		callback?: () => void
	) => void;
	addToBundle: (
		bundle: Bundle,
		projects: SelectedProject[],
		comparisons: SelectedComparison[]
	) => void;
	removeProjectFromBundle: (bundle: Bundle, project: ProjectDto) => void;
	removeComparisonFromBundle: (
		bundle: Bundle,
		comparison: ProjectComparisonDto
	) => void;
	clearBundleState: () => void;
}

export interface IBundleContentPageRouteParams {
	bundleId: string;
}

export function BundleContentPageView(
	props: IBundleContentPageStateProps
): ReactElement {
	const {
		bundle,
		selectedProjects,
		selectedComparisons,
		updateBundle,
		fetchBundleContent,
		updateProject,
		updateProjectBundle,
		updateComparison,
		updateComparisonBundle,
		addToBundle,
		removeProjectFromBundle,
		removeComparisonFromBundle,
		clearBundleState
	} = props;
	const { t } = useTranslation('myBosatlas');

	const { bundleId } = useParams<IBundleContentPageRouteParams>();
	const [textInputDialogState, setTextInputDialogState] =
		useState<ITextInputDialogProps>({
			open: false,
			title: '',
			label: '',
			onCancel: () => {},
			onClose: () => {},
			onConfirm: () => {}
		});
	const [selectionDialogOpen, setSelectionDialogOpen] =
		useState<boolean>(false);

	const [selectBundleDialogState, setSelectBundleDialogState] =
		useState<ISelectBundleDialogProps>({
			open: false,
			bundles: [],
			onClose: () => {},
			onSelect: () => {}
		});

	const history = useHistory();

	useEffect(() => {
		return () => clearBundleState();
	}, []);

	useEffect(() => {
		fetchBundleContent(bundleId);
	}, [bundleId]);

	function onProjectClick(project: ProjectDto): void {
		history.push(`/project/${project.id}`);
	}

	function onComparisonClick(comparison: ProjectComparisonDto): void {
		history.push(`/comparison/${comparison.id}`);
	}

	function handleComparisonUpdateRequest(
		comparison: ProjectComparisonDto
	): void {
		setTextInputDialogState({
			open: true,
			title: t('folder.modifyName'),
			defaultValue: comparison.title,
			onCancel: closeTextInputDialog,
			onClose: closeTextInputDialog,
			onConfirm: (value: string) => {
				closeTextInputDialog();
				updateComparisonTitle(comparison, value);
			}
		});
	}

	function startBundleEdit(): void {
		if (bundle) {
			setTextInputDialogState({
				open: true,
				title: t('folder.modifyFolderName'),
				defaultValue: bundle.title,
				confirmLabel: t('folder.adjustFolder'),
				onCancel: closeTextInputDialog,
				onClose: closeTextInputDialog,
				onConfirm: (value: string) => {
					closeTextInputDialog();
					updateBundle(bundle.id as string, value);
				}
			});
		}
	}

	function updateComparisonTitle(
		comparison: ProjectComparisonDto,
		title: string
	): void {
		const newComparison: ProjectComparisonDto = produce(
			comparison,
			(draft: ProjectComparisonDto) => {
				draft.title = title;
			}
		);
		updateComparison(newComparison);
	}

	function handleProjectUpdateRequest(project: ProjectDto): void {
		setTextInputDialogState({
			open: true,
			title: t('folder.modifyName'),
			defaultValue: project.title,
			onCancel: closeTextInputDialog,
			onClose: closeTextInputDialog,
			onConfirm: (value: string) => {
				closeTextInputDialog();
				updateProjectTitle(project, value);
			}
		});
	}

	function handleRemoveProjectFromBundle(project: ProjectDto): void {
		removeProjectFromBundle(bundle as Bundle, project);
	}

	function handleRemoveComparisonFromBundle(
		comparison: ProjectComparisonDto
	): void {
		removeComparisonFromBundle(bundle as Bundle, comparison);
	}

	function updateProjectTitle(project: ProjectDto, title: string): void {
		const newProject: ProjectDto = produce(project, (draft: ProjectDto) => {
			draft.title = title;
		});
		updateProject(newProject);
	}

	function closeTextInputDialog(): void {
		setTextInputDialogState({
			...textInputDialogState,
			open: false
		});
	}

	function startAddingContent(): void {
		setSelectionDialogOpen(true);
	}

	function saveBundle(): void {
		addToBundle(bundle as Bundle, selectedProjects, selectedComparisons);
		setSelectionDialogOpen(false);
	}

	function handleProjectChangeBundle(project: ProjectDto): void {
		const projectBundles: Bundle[] = project.bundles
			? project.bundles.map(bundleDtoToBundle)
			: [];
		setSelectBundleDialogState({
			open: true,
			bundles: projectBundles,
			onClose: closeSelectBundleDialog,
			onSelect: (bundles: Bundle[]) => {
				updateProjectBundle(project, bundles, () =>
					fetchBundleContent(bundleId)
				);
				closeSelectBundleDialog();
			}
		});
	}

	function handleComparisonChangeBundle(
		comparison: ProjectComparisonDto
	): void {
		const comparisonBundles: Bundle[] = comparison.bundles
			? comparison.bundles.map(bundleDtoToBundle)
			: [];
		setSelectBundleDialogState({
			open: true,
			bundles: comparisonBundles,
			onClose: closeSelectBundleDialog,
			onSelect: (bundles: Bundle[]) => {
				updateComparisonBundle(comparison, bundles, () =>
					fetchBundleContent(bundleId)
				);
				closeSelectBundleDialog();
			}
		});
	}

	function closeSelectBundleDialog(): void {
		setSelectBundleDialogState({ ...selectBundleDialogState, open: false });
	}

	return (
		<PageContainer id="bundle-content-overview">
			<BackButton />
			<LoadingContainer sections={['bundle']}>
				{bundle && (
					<BundleViewer
						bundle={bundle}
						projects={bundle.projects ? bundle.projects : []}
						comparisons={
							bundle.comparisons ? bundle.comparisons : []
						}
						onAddContent={startAddingContent}
						onComparisonClick={onComparisonClick}
						onComparisonChangeBundle={handleComparisonChangeBundle}
						onComparisonRemove={handleRemoveComparisonFromBundle}
						onComparisonUpdate={handleComparisonUpdateRequest}
						onProjectClick={onProjectClick}
						onProjectChangeBundle={handleProjectChangeBundle}
						onProjectRemove={handleRemoveProjectFromBundle}
						onProjectUpdate={handleProjectUpdateRequest}
						onEditBundleClick={startBundleEdit}
					/>
				)}
			</LoadingContainer>
			<DialogWindow fullScreen open={selectionDialogOpen}>
				<ProjectSelector
					selectedProjects={selectedProjects}
					selectedComparisons={selectedComparisons}
					onCancel={() => setSelectionDialogOpen(false)}
					onConfirm={() => saveBundle()}
					bundle={bundle as Bundle}
				/>
			</DialogWindow>
			<TextInputDialog {...textInputDialogState} />
			<SelectBundleDialog {...selectBundleDialogState} />
		</PageContainer>
	);
}

function mapStateToProps(state: AppState) {
	return {
		bundle: state.bosatlas.bundle,
		selectedProjects: state.projectSelector.selectedProjects,
		selectedComparisons: state.projectSelector.selectedComparisons
	};
}

function mapDispatchToProps(dispatch: AppThunkDispatch<BosatlasActions>) {
	return {
		updateBundle: (bundleId: string, title: string) =>
			dispatch(bosatlasUpdateBundle(bundleId, title)),
		fetchBundleContent: (bundleId: string) =>
			dispatch(bosatlasFetchBundle(bundleId)),
		removeComparison: (comparison: ProjectComparisonDto) =>
			dispatch(bosatlasComparisonDelete(comparison)),
		removeProject: (project: ProjectDto) =>
			dispatch(bosatlasProjectDelete(project)),
		updateComparison: (comparison: ProjectComparisonDto) =>
			dispatch(bosatlasComparisonUpdate(comparison)),
		updateComparisonBundle: (
			comparison: ProjectComparisonDto,
			bundles: Bundle[],
			callback?: () => void
		) =>
			dispatch(
				bosatlasComparisonUpdateBundle(comparison, bundles, callback)
			),
		updateProject: (project: ProjectDto) =>
			dispatch(bosatlasProjectUpdate(project)),
		updateProjectBundle: (
			project: ProjectDto,
			bundles: Bundle[],
			callback?: () => void
		) => dispatch(bosatlasProjectUpdateBundle(project, bundles, callback)),
		addToBundle: (
			bundle: Bundle,
			projects: SelectedProject[],
			comparisons: SelectedComparison[]
		) => dispatch(bosatlasAddToBundle(bundle, projects, comparisons)),
		removeProjectFromBundle: (bundle: Bundle, project: ProjectDto) =>
			dispatch(bosatlasRemoveProjectFromBundle(bundle, project)),
		removeComparisonFromBundle: (
			bundle: Bundle,
			comparison: ProjectComparisonDto
		) => dispatch(bosatlasRemoveComparisonFromBundle(bundle, comparison)),
		clearBundleState: () => dispatch(bosatlasClearBundle())
	};
}

export const BundleContentPage = connect(
	mapStateToProps,
	mapDispatchToProps
)(BundleContentPageView);
